import { useContext } from 'react';
import { motion } from 'framer-motion';
import { AuthContext } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { User, Mail, Building, Calendar } from 'lucide-react';

export default function Profile() {
  const { user } = useContext(AuthContext);

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="profile-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-heading font-bold text-4xl mb-2">Profile</h1>
          <p className="text-secondary">Manage your account information</p>
        </motion.div>

        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-8 max-w-2xl"
        >
          {/* Avatar */}
          <div className="flex items-center space-x-6 mb-8 pb-8 border-b border-white/10">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-brand-meta via-ai-glow to-brand-google flex items-center justify-center text-white font-heading font-bold text-4xl">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="font-heading font-bold text-3xl mb-1">{user?.name}</h2>
              <p className="text-secondary">AdPulse AI Member</p>
            </div>
          </div>

          {/* Information */}
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-secondary mb-1">Full Name</p>
                <p className="text-lg font-medium">{user?.name}</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-secondary mb-1">Email</p>
                <p className="text-lg font-medium">{user?.email}</p>
              </div>
            </div>

            {user?.company && (
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                  <Building className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-widest text-secondary mb-1">Company</p>
                  <p className="text-lg font-medium">{user?.company}</p>
                </div>
              </div>
            )}

            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-secondary mb-1">Member Since</p>
                <p className="text-lg font-medium">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Stats Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card p-8 max-w-2xl mt-6"
        >
          <h3 className="font-heading font-semibold text-xl mb-6">Account Activity</h3>
          <div className="grid grid-cols-3 gap-6">
            <div className="text-center">
              <p className="font-heading font-bold text-3xl text-ai-glow mb-1">Active</p>
              <p className="text-sm text-secondary">Status</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-bold text-3xl text-success mb-1">Pro</p>
              <p className="text-sm text-secondary">Plan</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-bold text-3xl text-brand-google mb-1">2</p>
              <p className="text-sm text-secondary">Platforms</p>
            </div>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
